import { useEffect, useState } from 'react';
import SitemapGenerator from '@/utils/sitemapGenerator';
import posts from '@/shared/api/posts';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { mockAPIResponse } from '@/utils/mockData';

const SitemapDebugPage = () => {
    const [sitemap, setSitemap] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [articles, setArticles] = useState([]);
    const [apiTest, setApiTest] = useState(null);

    useEffect(() => {
        testAPIAndGenerateSitemap();
    }, []);

    const testAPIAndGenerateSitemap = async () => {
        setLoading(true);
        setError(null);
        
        try {
            // Тестируем API
            console.log('Testing API...');
            let response;
            let useMock = false;
            
            try {
                response = await posts.getPublishedPosts(1, 10);
                console.log('API Response:', response);
                setApiTest(response);
                
                if (response && response.posts) {
                    setArticles(response.posts);
                    console.log(`Found ${response.posts.length} articles:`, response.posts.map(p => p.slug));
                }
            } catch (apiErr) {
                console.warn('API failed, using mock data:', apiErr);
                response = mockAPIResponse;
                setApiTest(mockAPIResponse);
                setArticles(mockAPIResponse.posts);
                useMock = true;
            }

            // Генерируем sitemap
            console.log('Generating sitemap...');
            const generator = new SitemapGenerator();
            const sitemapXml = await generator.generateSitemap();
            setSitemap(sitemapXml);
            console.log('Sitemap generated successfully');
            
            if (useMock) {
                toast.info('Использованы тестовые данные (API недоступен)');
            } else {
                toast.success('Sitemap успешно сгенерирован из реальных данных');
            }
            
        } catch (err) {
            console.error('Error:', err);
            setError(err.message || err.toString());
            toast.error('Ошибка при тестировании API или генерации sitemap');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div style={{ padding: '20px' }}>
                <h1>Sitemap Debug</h1>
                <p>Тестирование API и генерация sitemap...</p>
            </div>
        );
    }

    return (
        <div style={{ padding: '20px', maxWidth: '1200px' }}>
            <h1>Sitemap Debug</h1>
            
            <div className="mb-4">
                <Button onClick={testAPIAndGenerateSitemap} disabled={loading}>
                    Обновить
                </Button>
            </div>

            {/* API Test Results */}
            <Card className="mb-6">
                <CardHeader>
                    <CardTitle>Тест API (posts.getPublishedPosts)</CardTitle>
                </CardHeader>
                <CardContent>
                    {apiTest ? (
                        <div>
                            <p><strong>Статус:</strong> ✅ Успешно</p>
                            <p><strong>Источник данных:</strong> {apiTest === mockAPIResponse ? '🧪 Mock данные' : '🌐 Реальный API'}</p>
                            <p><strong>Найдено статей:</strong> {apiTest.posts?.length || 0}</p>
                            <p><strong>Всего страниц:</strong> {apiTest.totalPages || 0}</p>
                            <p><strong>Текущая страница:</strong> {apiTest.currentPage || 1}</p>
                            
                            {apiTest.posts && apiTest.posts.length > 0 && (
                                <div className="mt-4">
                                    <h4 className="font-medium">Статьи:</h4>
                                    <ul className="list-disc pl-5">
                                        {apiTest.posts.map((article, index) => (
                                            <li key={index} className="mb-2">
                                                <strong>{article.title}</strong><br />
                                                <span className="text-sm text-gray-600">
                                                    /article/{article.slug}<br />
                                                    Создана: {new Date(article.createdAt).toLocaleDateString()}<br />
                                                    Опубликовано: {article.published ? '✅' : '❌'}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            
                            {apiTest.posts?.length === 0 && (
                                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
                                    <p className="text-yellow-800">
                                        <strong>⚠️ Внимание!</strong> API вернул пустой список статей. 
                                        Возможные причины:
                                    </p>
                                    <ul className="list-disc pl-5 mt-2 text-sm">
                                        <li>Нет опубликованных статей в базе</li>
                                        <li>API endpoint неверный</li>
                                        <li>Проблемы с подключением к базе данных</li>
                                        <li>Статьи имеют статус published: false</li>
                                    </ul>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div>
                            <p>❌ API не отвечает или ошибка</p>
                            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded">
                                <p className="text-red-800 text-sm">
                                    <strong>Возможные решения:</strong>
                                </p>
                                <ul className="list-disc pl-5 mt-2 text-sm">
                                    <li>Проверьте что backend запущен</li>
                                    <li>Проверьте переменную REACT_APP_BACKEND_URL</li>
                                    <li>Проверьте доступность API endpoint</li>
                                    <li>Посмотрите консоль браузера для деталей ошибки</li>
                                </ul>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Error Display */}
            {error && (
                <Card className="mb-6 border-red-200">
                    <CardHeader>
                        <CardTitle className="text-red-600">Ошибка</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <pre className="bg-red-50 p-4 rounded text-sm overflow-auto">
                            {error}
                        </pre>
                    </CardContent>
                </Card>
            )}

            {/* Sitemap Preview */}
            <Card>
                <CardHeader>
                    <CardTitle>Предпросмотр Sitemap XML</CardTitle>
                </CardHeader>
                <CardContent>
                    <details>
                        <summary>Показать XML ({sitemap.length} символов)</summary>
                        <pre className="bg-gray-50 p-4 rounded text-xs overflow-auto max-h-96 whitespace-pre-wrap font-mono">
                            {sitemap}
                        </pre>
                    </details>
                </CardContent>
            </Card>
        </div>
    );
};

export default SitemapDebugPage;
